import React, { useEffect, useMemo, useState } from "react";
import {
  Datagrid,
  DateField,
  List,
  Loading,
  TextField,
  Filter,
  SearchInput,
  FunctionField,
  useListContext,
  useListController,
} from "react-admin";
import { ComponentProps } from "../../providers/Interfaces";
import { exportToExcel } from "../../utils/Helper";

const fields = [
  "id",
  "user",
  "scanset",
  "created_at",
  "email",
  "employee_number",
  "industry_partner",
  "store",
  "type",
  "order_nr",
  "event_name",
];

const normalizeValue = (value: unknown): string =>
  String(value === undefined || value === null ? "" : value).toLowerCase();

const matchesCartSearch = (record: any, searchTerm: string): boolean => {
  if (!record) return false;

  const personalInfo = record.personal_info || {};
  const firstName = normalizeValue(personalInfo.first_name);
  const lastName = normalizeValue(personalInfo.last_name);
  const fullName = `${firstName} ${lastName}`.trim();
  const reversedFullName = `${lastName} ${firstName}`.trim();

  const additionalFields = [
    record.id !== undefined ? String(record.id) : "",
    record.industry_partner,
    record.store,
    record.type,
    record.device,
    record.event_name,
    record.order_nr,
  ].map((value) => normalizeValue(value));

  return (
    firstName.includes(searchTerm) ||
    lastName.includes(searchTerm) ||
    fullName.includes(searchTerm) ||
    reversedFullName.includes(searchTerm) ||
    additionalFields.some((fieldValue) => fieldValue.includes(searchTerm))
  );
};

const CartsFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput
      source="searchTerm"
      alwaysOn
      placeholder="Search"
      filterToQuery={(value: string) => {
        const trimmed = value?.trim();
        if (!trimmed) {
          return { searchTerm: undefined };
        }

        return { searchTerm: trimmed };
      }}
    />
  </Filter>
);

const CartsDatagrid = ({ children, ...props }: any) => {
  const { ids, data, filterValues } = useListContext();
  const searchTerm =
    filterValues && filterValues.searchTerm
      ? String(filterValues.searchTerm).toLowerCase()
      : "";

  const filteredIds = useMemo(() => {
    if (!ids || !ids.length) {
      return ids;
    }

    if (!searchTerm) {
      return ids;
    }

    return ids.filter((id: string | number) => {
      const record = data[id];
      if (!record) return false;

      return matchesCartSearch(record, searchTerm);
    });
  }, [ids, data, searchTerm]);

  return (
    <Datagrid {...props} ids={filteredIds} data={data}>
      {children}
    </Datagrid>
  );
};

export const CartsOverviewList = (props: ComponentProps): JSX.Element => {
  const { permissions } = props;
  const controllerProps = useListController(props);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (permissions !== undefined) {
      setLoading(false);
    }
  }, [permissions]);

  if (loading) return <Loading />;

  const currentSearchValue = controllerProps.filterValues
    ? controllerProps.filterValues.searchTerm
    : undefined;
  const normalizedSearch = currentSearchValue
    ? String(currentSearchValue).toLowerCase()
    : "";

  return (
    <List
      {...props}
      bulkActionButtons={false}
      exporter={(records: any[]) => {
        const filteredRecords = normalizedSearch
          ? records.filter((record) =>
              matchesCartSearch(record, normalizedSearch)
            )
          : records;
        exportToExcel(filteredRecords, fields, "Carts");
      }}
      sort={{ field: "id" }}
      title="Carts"
      perPage={1000}
      filters={<CartsFilter />}
    >
      <CartsDatagrid rowClick="show">
        <TextField source="id" />
        <TextField label="First Name" source="personal_info.first_name" />
        <TextField label="Last Name" source="personal_info.last_name" />
        <TextField label="Industry Partner" source="industry_partner" />
        <TextField label="Store" source="store" />
        <TextField label="Cart Type" source="type" />
        <TextField label="Device" source="device" />
        <TextField label="Event Name" source="event_name" />
        <FunctionField
          label="Scanset"
          render={(record: any) =>
            record && record.scanset ? (
              <a
                href={`/#/scans/${record.scanset}/show`}
                onClick={(event) => event.stopPropagation()}
              >
                {record.scanset}
              </a>
            ) : (
              "-"
            )
          }
        />
        <DateField
          source="created_at"
          locales="en-DE"
          options={{
            hour: "2-digit",
            minute: "2-digit",
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour12: false,
          }}
          showTime
        />
      </CartsDatagrid>
    </List>
  );
};
